import { apiClient, type ApiEnvelope } from '@/shared/api/client';
import { useAuthStore } from '@/shared/stores/auth';

/**
 * קו SIP אחד לכל משתמש — בין מכשירים (השלמה ל-sipTabLock, שמגן רק בין
 * לשוניות של אותו דפדפן).
 *
 * 🔑 29/09/2026, נציג 813: שני סופטפונים על אותה שלוחה העיפו זה את זה כל כמה
 * דקות באסטריסק, והשיחה נענתה אוטומטית במכשיר שהנציג לא הקשיב לו. השרת
 * (SipLineClaimController) מכריע מי מחזיק: המחזיק מחדש כל 10ש. כניסה חדשה
 * (טעינת דף) לוקחת את הקו בכוח והקודם מתנתק לבד; חיבור חוזר (ניתוק רשת) לא
 * לוקח בכוח — נדחה עד שהמחזיק שותק 75ש או משחרר (ראה claimedThisPage).
 *
 * כשל רשת/שרת ⇒ "בעלים" (fail-open): עדיף סיכון לשני מכשירים מאשר נציג בלי קו.
 */

export interface OtherDevice {
  label: string | null;
  ip: string | null;
  since: string | null;
}

export type ClaimResult = { owner: true } | { owner: false; other: OtherDevice };

/**
 * חידוש מהיר: המכשיר שהודח חייב לרדת מהקו לפני ה-re-REGISTER הבא שלו
 * באסטריסק, אחרת הוא מעיף בחזרה את החדש (remove_existing).
 */
export const CLAIM_RENEW_MS = 10_000;

const DEVICE_KEY = 'gocall-device-id';
let cachedDeviceId: string | null = null;

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/** מזהה קבוע לדפדפן (משותף לכל הלשוניות — ביניהן מכריע sipTabLock). */
export function deviceId(): string {
  if (cachedDeviceId) return cachedDeviceId;
  try {
    const saved = localStorage.getItem(DEVICE_KEY);
    if (saved) return (cachedDeviceId = saved);
    const id = randomId();
    localStorage.setItem(DEVICE_KEY, id);
    return (cachedDeviceId = id);
  } catch {
    return (cachedDeviceId = randomId());
  }
}

/** תיאור קצר שהמכשיר השני יציג לנציג ("Chrome · Windows"). */
export function deviceLabel(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'דפדפן';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} · ${os}` : browser;
}

export async function claimSipLine(force = false): Promise<ClaimResult> {
  try {
    const { data } = await apiClient.post<ApiEnvelope<{ owner: boolean; other?: OtherDevice }>>(
      '/agent/sip-line/claim',
      { device_id: deviceId(), force, label: deviceLabel() },
    );
    const d = data?.data;
    if (d && d.owner === false) {
      return { owner: false, other: d.other ?? { label: null, ip: null, since: null } };
    }
    return { owner: true };
  } catch {
    return { owner: true };
  }
}

/** בסגירת הדפדפן: משחררים מיד כדי שמכשיר אחר לא יחכה 75ש. keepalive שורד את הסגירה. */
export function releaseSipLineOnUnload(): void {
  try {
    const { token, tenantSlug } = useAuthStore.getState();
    if (!token) return;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
    void fetch(`${apiClient.defaults.baseURL ?? ''}/agent/sip-line/release`, {
      method: 'POST',
      keepalive: true,
      headers,
      body: JSON.stringify({ device_id: deviceId() }),
    }).catch(() => { /* הדף נסגר — לא משנה */ });
  } catch {
    /* אין fetch/אחסון — השרת ישחרר לבד אחרי 75ש */
  }
}

export function otherDeviceMessage(other: OtherDevice): string {
  const where = other.label ? ` (${other.label})` : '';
  return `התחברת ממחשב או דפדפן אחר${where} — הקו עבר לשם והחיבור כאן נותק. לחץ "העבר לכאן" כדי לעבוד מכאן.`;
}

/** לטסטים בלבד. */
export function __resetDeviceIdForTests(): void {
  cachedDeviceId = null;
}
